// «Cerrar» y «Cerrar los N» de «Han terminado» (flujo.md #6, Entrega 3): un clic, sin diálogo, con un aviso «Deshacer»
// de 6 s (como el de mover mesas). Cerrar le manda al agente su orden de cierre, que no se puede retirar: por eso la
// orden NO sale hasta que pasan los 6 s sin «Deshacer» (como «deshacer enviar» del correo). Mientras tanto la fila se
// queda en su sitio con el botón girando; sale de su bloque cuando el servidor lo confirma (llega por el socket). Si
// falla, el aviso dice por qué y la fila vuelve a tener su «Cerrar». El diálogo largo queda para «Despedir sin recoger».
//
// Estado propio y efímero (fuente única: este almacén): qué cierres esperan sus 6 s, cuáles se están mandando y cuáles
// ya salieron. Lo del servidor (ciclo, situación) no se toca: llega como siempre.
//
// 05-11 (auditoría, QA G1/G2) — «el mundo cambia mientras Pau actúa»:
// - UN reloj. Antes el aviso (sonner) y la orden llevaban relojes distintos: sonner pausa el suyo con el ratón encima o
//   con la pestaña oculta, el nuestro no; a los 6 s salía la orden y «Deshacer» seguía a la vista y mentía. Ahora el aviso
//   no tiene reloj propio (dura hasta que lo quitamos) y el único reloj es este: al cumplirse, el lote deja de estar
//   vigente, el aviso se quita y DESPUÉS sale la orden. «Deshacer» de un lote que ya no está vigente no hace nada.
// - La orden se decide con lo de AHORA, no con lo del clic: antes de mandarla se mira otra vez cada agente en el almacén
//   de la oficina. Si ya no ha terminado (te ha escrito, pregunta algo, vuelve a trabajar, quiere subir algo) o ha dicho
//   algo nuevo desde el clic, no se cierra y el aviso lo dice.
// - «Enviado» no gira para siempre: se apaga en cuanto el servidor confirma (cambia su ciclo o su situación, o se va de
//   la oficina) y, como mucho, a los `ESPERA_CONFIRMACION_MS`.
import { create } from 'zustand';
import { t } from '../../../i18n';
import { useToastStore } from '../../../stores/toast.store';
import { oficinaApi } from '../api';
import type { ContratadoDTO } from '../compartido/vocabulario';
import { useBandejaStore } from '../despliegues/store';
import { subidasPorAgente } from '../despliegues/subidas';
import { mensajeError } from '../errores';
import { quien } from '../marco/estado';
import { bloqueDe, grupoDelContratado, haTerminado, seVa, type SantiDelAgente } from '../marco/situacion';
import { santiPorAgente } from '../pelotas/porAgente';
import { usePelotasStore } from '../pelotas/store';
import { useOficinaStore } from '../store';

export const DESHACER_CIERRE_MS = 6_000;
/** Tras mandar la orden, lo más que el botón gira esperando a que el servidor lo confirme por el socket. */
export const ESPERA_CONFIRMACION_MS = 60_000;

export type FaseCierre = 'espera' | 'enviando' | 'enviado';

interface EstadoCierres {
  fases: Record<string, FaseCierre>;
}

export const useCierresStore = create<EstadoCierres>(() => ({ fases: {} }));

const poner = (ids: readonly string[], fase: FaseCierre | null) =>
  useCierresStore.setState((s) => {
    const fases = { ...s.fases };
    for (const id of ids) {
      if (fase) fases[id] = fase;
      else delete fases[id];
    }
    return { fases };
  });

/** La fase del cierre de un agente (null = ninguno en marcha). */
export const useFaseCierre = (agentId: string): FaseCierre | null => useCierresStore((s) => s.fases[agentId] ?? null);

/** Por qué no se cierra a alguien al cumplirse los 6 s (null = se cierra). */
export type MotivoNoCerrar = 'subida' | 'escrito' | 'trabaja' | 'cambio';

/**
 * PURO. ¿Sigue igual que cuando Pau pulsó «Cerrar»? `antes` es la fila que vio; `ahora`, la de este momento. Se cierra
 * solo si sigue habiendo terminado, nada le toca a Pau (subida, pregunta, permiso) y no ha dicho nada nuevo desde el clic.
 */
export function motivoParaNoCerrar(antes: ContratadoDTO, ahora: ContratadoDTO, santi: SantiDelAgente | undefined, ctx: { subida: { creadoEn: string } | null; ahora: number }): MotivoNoCerrar | null {
  const grupo = grupoDelContratado(ahora, santi, ctx);
  if (grupo === 'subida') return 'subida';
  if (bloqueDe(grupo) === 'te_toca') return 'escrito';
  if (!haTerminado(ahora)) return ahora.situacion === 'trabajando' || ahora.situacion === 'llegando' ? 'trabaja' : 'cambio';
  const nuevo = ahora.ultimoMensaje?.en !== antes.ultimoMensaje?.en || ahora.teNecesitaDesde !== antes.teNecesitaDesde || ahora.ultimoInformeEn !== antes.ultimoInformeEn;
  return nuevo ? 'escrito' : null;
}

/** Cierra a estos agentes tras 6 s salvo «Deshacer». Los que ya tienen un cierre en marcha no se repiten. */
export function cerrarConDeshacer(agentes: readonly ContratadoDTO[]): void {
  const fases = useCierresStore.getState().fases;
  // Lo que Pau vio al pulsar: con esto se compara a los 6 s.
  const lote = agentes.filter((c) => !fases[c.agentId]);
  if (!lote.length) return;
  const ids = lote.map((c) => c.agentId);
  poner(ids, 'espera');
  let vigente = true;
  // Sin reloj propio (0 = hasta que se quite): el único reloj es el temporizador de abajo.
  const aviso = useToastStore.getState().push({
    message: lote.length === 1 ? t('oficinaV2.lista.cierre.uno', { nombre: quien(lote[0]!) }) : t('oficinaV2.lista.cierre.varios', { n: lote.length }),
    durationMs: 0,
    action: {
      label: t('oficinaV2.lista.cierre.deshacer'),
      onClick: () => {
        // La orden ya salió (o está saliendo): no se puede retirar, así que «Deshacer» no hace nada.
        if (!vigente) return;
        vigente = false;
        clearTimeout(temporizador);
        poner(ids, null);
        useToastStore.getState().dismiss(aviso);
      },
    },
  });
  const temporizador = setTimeout(() => {
    if (!vigente) return;
    vigente = false;
    // Primero se quita el aviso (y con él «Deshacer»); después sale la orden.
    useToastStore.getState().dismiss(aviso);
    void mandar(lote);
  }, DESHACER_CIERRE_MS);
}

/** Lo que la lista sabe ahora de Santi y de las subidas, sin React (lo mismo que useEquipo). */
function contextoActual(): { santi: ReadonlyMap<string, SantiDelAgente>; subidas: ReadonlyMap<string, { creadoEn: string }> } {
  const { contratados, grupos } = useOficinaStore.getState();
  const tablero = usePelotasStore.getState().lista;
  const santi = tablero?.configurado ? santiPorAgente(tablero.contratos, grupos, Object.values(contratados)) : new Map<string, SantiDelAgente>();
  const subidas = subidasPorAgente(useBandejaStore.getState().bandeja?.pendientes ?? []);
  return { santi, subidas };
}

async function mandar(lote: readonly ContratadoDTO[]): Promise<void> {
  const { contratados } = useOficinaStore.getState();
  const { santi, subidas } = contextoActual();
  const ahora = Date.now();
  const salen: ContratadoDTO[] = [];
  const cambiados: { c: ContratadoDTO; motivo: MotivoNoCerrar }[] = [];
  for (const antes of lote) {
    const vivo = contratados[antes.agentId];
    // Ya no está en la oficina, o ya se está yendo por otro lado: no hay nada que cerrar (ni que decir).
    if (!vivo || seVa(vivo)) {
      poner([antes.agentId], null);
      continue;
    }
    const motivo = motivoParaNoCerrar(antes, vivo, santi.get(vivo.agentId), { subida: subidas.get(vivo.agentId) ?? null, ahora });
    if (motivo) {
      poner([vivo.agentId], null);
      cambiados.push({ c: vivo, motivo });
    } else salen.push(vivo);
  }
  if (cambiados.length) {
    useToastStore.getState().push({
      message:
        cambiados.length === 1
          ? t(`oficinaV2.lista.cierre.noCerrado.${cambiados[0]!.motivo}`, { nombre: quien(cambiados[0]!.c) })
          : t('oficinaV2.lista.cierre.noCerradoVarios', { nombres: cambiados.map((x) => `«${quien(x.c)}»`).join(', ') }),
      durationMs: 8_000,
    });
  }
  poner(
    salen.map((c) => c.agentId),
    'enviando',
  );
  await Promise.all(
    salen.map(async (c) => {
      try {
        await oficinaApi.cerrar(c.agentId);
        esperarConfirmacion(c);
      } catch (e) {
        poner([c.agentId], null);
        useToastStore.getState().push({ message: t('oficinaV2.lista.cierre.error', { nombre: quien(c), motivo: mensajeError(e) }) });
      }
    }),
  );
}

// ── «Enviado»: hasta que el servidor lo confirme (o se acabe la espera) ─────────────────────────────────────────────
// Una sola suscripción al almacén de la oficina mientras haya alguno esperando; se suelta cuando no queda ninguno.

const esperando = new Map<string, { ciclo: ContratadoDTO['ciclo']; situacion: ContratadoDTO['situacion']; reloj: ReturnType<typeof setTimeout> }>();
let soltarSuscripcion: (() => void) | null = null;

function dejarDeEsperar(agentId: string): void {
  const e = esperando.get(agentId);
  if (!e) return;
  clearTimeout(e.reloj);
  esperando.delete(agentId);
  if (useCierresStore.getState().fases[agentId] === 'enviado') poner([agentId], null);
  if (!esperando.size && soltarSuscripcion) {
    soltarSuscripcion();
    soltarSuscripcion = null;
  }
}

function esperarConfirmacion(c: ContratadoDTO): void {
  dejarDeEsperar(c.agentId);
  poner([c.agentId], 'enviado');
  esperando.set(c.agentId, { ciclo: c.ciclo, situacion: c.situacion, reloj: setTimeout(() => dejarDeEsperar(c.agentId), ESPERA_CONFIRMACION_MS) });
  soltarSuscripcion ??= useOficinaStore.subscribe((s) => {
    for (const [id, e] of [...esperando]) {
      const ahora = s.contratados[id];
      if (!ahora || ahora.ciclo !== e.ciclo || ahora.situacion !== e.situacion) dejarDeEsperar(id);
    }
  });
}
